import React, { useEffect, useRef, useState } from "react";
import { SettingsDropdownHeader } from "@/components/layout/Sidebar/SettingsDropdownHeader";
import { ControlSlider } from "@/components/ui-kit/Inputs/ControlSlider";
import { ToggleSwitch } from "@/components/ui-kit/Inputs/ToggleSwitch";
import autoAnimate from "@formkit/auto-animate";
import { PrimaryTinyButton } from "@/components/ui-kit/Buttons/PrimaryTinyButton";
import { RestrictedGroup } from "@/components/ui-kit/RestrictedGroup";
import { SettingsDropdownNavigationProps } from "@/types/layout";
import useUserProvider from "@/core/dataProviders/providers/auth/useUserProvider";
import { useDarkTheme } from "@/hooks/useDarkTheme";
import { getCookie, hasCookie, setCookie } from "cookies-next";
import { pushToDataLayer } from "@/utils/pushToDataLayer";
import useUserSubscriptionProvider from "@/core/dataProviders/providers/auth/useUserSubscriptionProvider";
import { FEATURE_PERMISSIONS } from "@/core/types/permissions";
import { wrapper } from "@/core/store/store";
import Link from "next/link";
import { useAuthUrls } from "@/hooks/useAuthUrls";
import useSubscriptionPermissionsChecker from "@/hooks/useSubscriptionPermissionsChecker";
import { UseSubsPermissionsActionTypes } from "@/types/hooks";
import { useFirebase } from "@/hooks/useFirebase";

export const SettingsDropdownNavigation: React.FC<SettingsDropdownNavigationProps> = ({ prompt, promptToInstall }) => {
  const [toggleAccordion, setToggleAccordion] = useState<boolean>(false);
  const [isVisiblePwa, setIsVisiblePwa] = useState(false);
  const [fontSizeValue, setFontSizeValue] = useState<number>(18);
  const parent = useRef(null);
  const { store } = wrapper.useWrappedStore({});
  const { getFCMToken, deleteFCMToken } = useFirebase();

  const { currentTheme, switchTheme } = useDarkTheme();
  const [subscriptions] = useUserSubscriptionProvider();
  const [user] = useUserProvider();
  const { getSubscriptionUrl } = useAuthUrls();

  const isFeaturePermission = useSubscriptionPermissionsChecker(
    UseSubsPermissionsActionTypes.VALID_PERMISSIONS,
    FEATURE_PERMISSIONS.DARK_MODE && FEATURE_PERMISSIONS.FONT_SIZE
  );

  useEffect(() => {
    parent.current && autoAnimate(parent.current);
  }, [parent]);

  useEffect(() => {
    const inPwa = window.matchMedia("(display-mode: standalone)").matches;
    const userAgent = navigator.userAgent || null;
    const isIos = Boolean(userAgent?.match(/iPhone|iPad|iPod/i));
    const isAndroid = Boolean(userAgent?.match(/android/i));

    if (prompt !== null && !inPwa) {
      pushToDataLayer({
        event: "pwa_interaction",
        source_device: isIos ? "iphone" : isAndroid ? "android" : "",
        interaction_type: "challenge",
      });

      setIsVisiblePwa(true);
      return;
    }

    setIsVisiblePwa(false);
  }, [prompt]);

  useEffect(() => {
    if (hasCookie("fontSize") && getCookie("fontSize")) {
      applyFontSize(+getCookie("fontSize")!);
    }
  }, []);

  const applyFontSize = (size: number) => {
    document.documentElement.style.setProperty("--content-font-size", `${size}px`);
    setCookie("fontSize", size);
    setFontSizeValue(size);
  };

  const installPwa = () => {
    const userAgent = navigator.userAgent || null;
    const isIos = Boolean(userAgent?.match(/iPhone|iPad|iPod/i));
    const isAndroid = Boolean(userAgent?.match(/android/i));
    const isMacOs = Boolean(userAgent?.match(/mac/i));
    const isWindows = Boolean(userAgent?.match(/win/i));

    pushToDataLayer({
      event: "pwa_interaction",
      source_device: isIos ? "iphone" : isAndroid ? "android" : isMacOs ? "macos" : isWindows ? "windows" : "",
      interaction_type: "install_click",
    });

    promptToInstall();
    setIsVisiblePwa(false);
  };

  return (
    <div
      ref={parent}
      className="static border-b border-gray-200 py-[16px] font-condensed text-md font-light dark:border-white-25 lg:relative"
    >
      <SettingsDropdownHeader
        title="Nastavení"
        toggleAccordion={toggleAccordion}
        setToggleAccordion={setToggleAccordion}
      />
      {toggleAccordion && (
        <div className={`overflow-hidden px-5 pt-4`}>
          {isVisiblePwa && !window?.matchMedia("(display-mode: standalone)")?.matches && (
            <div className="mb-4 flex">
              <div>
                <div className="font-condensed text-md font-light text-dark dark:text-white">Přidat na plochu</div>
                <p className="font-sans text-xs tracking-[0.25px] text-dark-70 dark:text-white-70">
                  Rychlý přístup k magazínu z plochy vašeho zařízení
                </p>
              </div>
              <div>
                <PrimaryTinyButton onClick={installPwa} title="Přidat" />
              </div>
            </div>
          )}
          <ToggleSwitch
            title="Notifikace"
            description="Dáme vám vědět, když se stane něco výjimečného. Třeba skvělý článek nebo nový podcast."
            onChange={(value) => {
              if (value) {
                return getFCMToken();
              }
              return deleteFCMToken();
            }}
            value={!!localStorage.getItem("firebase-push-token")}
          />
          <RestrictedGroup
            title={
              <Link
                href={getSubscriptionUrl()}
                className="text-dark underline decoration-transparent decoration-1 ease-in-out hover:decoration-dark-70 dark:text-white"
              >
                Pro všechny předplatitele
              </Link>
            }
            allowed={isFeaturePermission}
          >
            <ToggleSwitch title="Tmavý režim" value={currentTheme === "dark"} onChange={switchTheme} />
            <ControlSlider
              title="Velikost písma"
              leftLabel="Aa"
              rightLabel="Aa"
              value={fontSizeValue}
              onChange={(value) => applyFontSize(value as number)}
            />
          </RestrictedGroup>
          {!subscriptions?.length && (
            <RestrictedGroup
              title={
                <Link
                  href={getSubscriptionUrl()}
                  className="text-dark underline decoration-transparent decoration-1 ease-in-out hover:decoration-dark-70 dark:text-white"
                >
                  Navíc s balíčkem Premium
                </Link>
              }
              allowed={false}
            >
              <div className="flex w-full justify-between pb-3">
                <div className="pt-0.5 font-condensed text-md font-light text-dark dark:text-white">
                  Vypnout reklamu
                </div>
                <div>
                  <PrimaryTinyButton onClick={() => {}} title="Vypnout" />
                </div>
              </div>
            </RestrictedGroup>
          )}
        </div>
      )}
    </div>
  );
};
